/**
 * Feitelijke achtergronddata per betaalmethode. Deze cijfers komen uit
 * publieke bronnen (jaarverslagen, brancherapportages en de documentatie van
 * de betaaldienstverlener zelf) en worden door de factchecker gecontroleerd.
 */
export type MethodFacts = {
  slug: string;
  /** Eigenaar of scheme-houder van de betaalmethode. */
  operator: string;
  /** Jaar van introductie in Nederland. */
  launched: string;
  headquarters: string;
  /** Type betaalstroom: push, pull of prepaid. */
  flow: string;
  settlement: string;
  currencies: string;
  strongAuth: string;
  chargeback: string;
  /** Kosten voor de speler. */
  playerCost: string;
  /** Wat het casino zelf betaalt per transactie. */
  merchantCost: string;
  /** Feiten in één zin, met bron. */
  keyFacts: { label: string; value: string; source: string }[];
  sources: { label: string; url: string }[];
};

export const METHOD_FACTS: MethodFacts[] = [
  {
    slug: "ideal",
    operator: "Currence iDEAL / European Payments Initiative (EPI)",
    launched: "2005",
    headquarters: "Amsterdam, Nederland",
    flow: "Push: de speler geeft zelf opdracht in de eigen bankomgeving",
    settlement: "Directe bijschrijving, saldo definitief binnen enkele seconden",
    currencies: "Alleen euro",
    strongAuth: "Sterke klantauthenticatie via bank-app, scanner of identifier",
    chargeback: "Geen chargeback: een geslaagde iDEAL-betaling is onherroepelijk",
    playerCost: "Gratis voor de speler bij vrijwel alle Nederlandse banken",
    merchantCost: "Circa € 0,20 – € 0,45 per transactie voor de aanbieder",
    keyFacts: [
      { label: "Transacties per jaar", value: "ruim 1,3 miljard iDEAL-betalingen in Nederland", source: "Currence iDEAL jaarcijfers" },
      { label: "Aandeel online betalingen", value: "ongeveer 70% van alle Nederlandse webbetalingen", source: "Betaalvereniging Nederland" },
      { label: "Deelnemende banken", value: "alle grote Nederlandse banken, inclusief bunq, Knab en Revolut NL", source: "iDEAL bankenoverzicht" },
      { label: "Overstap", value: "iDEAL migreert stapsgewijs naar Wero, het EPI-wallet-platform", source: "EPI persbericht" },
    ],
    sources: [
      { label: "Currence iDEAL", url: "https://www.ideal.nl" },
      { label: "Betaalvereniging Nederland", url: "https://www.betaalvereniging.nl" },
    ],
  },
  {
    slug: "trustly",
    operator: "Trustly Group AB",
    launched: "2008 (Nederland vanaf 2014)",
    headquarters: "Stockholm, Zweden",
    flow: "Open banking: account-to-account via PSD2-koppeling",
    settlement: "Storting direct, opname als bankoverboeking naar dezelfde rekening",
    currencies: "Euro en 29 andere valuta",
    strongAuth: "Inloggen bij de eigen bank met tweefactorauthenticatie",
    chargeback: "Geen chargeback; wel terugboekrecht bij ongeautoriseerde betalingen",
    playerCost: "Gratis voor de speler",
    merchantCost: "Doorgaans 0,6% – 1,2% van het transactiebedrag",
    keyFacts: [
      { label: "Vergunning", value: "betaalinstelling onder toezicht van de Zweedse Finansinspektionen", source: "Finansinspektionen register" },
      { label: "Bereik", value: "koppeling met meer dan 6.300 banken in Europa en Noord-Amerika", source: "Trustly bedrijfsinformatie" },
      { label: "Uitbetaling", value: "Trustly Payouts schrijft doorgaans binnen enkele uren bij", source: "Trustly productdocumentatie" },
      { label: "Verificatie", value: "de bankgegevens bevestigen naam en rekening, wat KYC versnelt", source: "PSD2-richtlijn" },
    ],
    sources: [
      { label: "Trustly", url: "https://www.trustly.com" },
      { label: "PSD2 (EU 2015/2366)", url: "https://eur-lex.europa.eu" },
    ],
  },
  {
    slug: "apple-pay",
    operator: "Apple Inc.",
    launched: "Nederland vanaf juni 2019",
    headquarters: "Cupertino, Verenigde Staten",
    flow: "Tokenized kaartbetaling via Wallet",
    settlement: "Direct; de onderliggende kaart wordt binnen seconden belast",
    currencies: "Valuta van de gekoppelde kaart",
    strongAuth: "Face ID, Touch ID of toegangscode per betaling",
    chargeback: "Ja, via de kaartuitgever (Visa/Mastercard-regels)",
    playerCost: "Geen extra kosten bovenop de kaartvoorwaarden",
    merchantCost: "Kaartafhandelingskosten, meestal 1% – 2%",
    keyFacts: [
      { label: "Beveiliging", value: "het kaartnummer wordt vervangen door een device-specifiek token (DAN)", source: "Apple Platform Security Guide" },
      { label: "Uitbetaling", value: "opnemen kan niet naar Apple Pay; casino's boeken terug naar bank of kaart", source: "Eigen kassatest" },
      { label: "Ondersteuning", value: "werkt in Safari op Mac en op iPhone/iPad met iOS 12 of nieuwer", source: "Apple supportdocumentatie" },
      { label: "Banken", value: "vrijwel alle Nederlandse banken ondersteunen Apple Pay", source: "Betaalvereniging Nederland" },
    ],
    sources: [
      { label: "Apple Pay", url: "https://www.apple.com/nl/apple-pay/" },
      { label: "Apple Platform Security", url: "https://support.apple.com/guide/security/" },
    ],
  },
  {
    slug: "google-pay",
    operator: "Google Payment Ltd. (Ierland voor de EU)",
    launched: "Nederland vanaf oktober 2019",
    headquarters: "Mountain View, Verenigde Staten",
    flow: "Tokenized kaartbetaling via Google Wallet",
    settlement: "Direct via de gekoppelde debit- of creditcard",
    currencies: "Valuta van de gekoppelde kaart",
    strongAuth: "Schermvergrendeling, vingerafdruk of pincode",
    chargeback: "Ja, via de kaartuitgever",
    playerCost: "Geen extra kosten",
    merchantCost: "Kaartafhandelingskosten, meestal 1% – 2%",
    keyFacts: [
      { label: "Beveiliging", value: "betaalt met een virtueel accountnummer, niet met je echte kaartnummer", source: "Google Wallet Help" },
      { label: "Toestellen", value: "Android 9 of hoger met Google Play-services; ook in Chrome op desktop", source: "Google supportdocumentatie" },
      { label: "Uitbetaling", value: "geen uitbetaling naar de wallet: winst gaat terug naar bank of kaart", source: "Eigen kassatest" },
      { label: "Limiet", value: "de bestedingslimiet is die van de onderliggende kaart, niet van Google", source: "Kaartvoorwaarden banken" },
    ],
    sources: [
      { label: "Google Wallet", url: "https://wallet.google" },
      { label: "Google Pay Help", url: "https://support.google.com/googlepay" },
    ],
  },
  {
    slug: "paypal",
    operator: "PayPal (Europe) S.à r.l. et Cie, S.C.A.",
    launched: "Nederland vanaf 2004",
    headquarters: "Luxemburg (EU-entiteit)",
    flow: "Wallet: saldo, gekoppelde bankrekening of kaart",
    settlement: "Storting direct; opname naar PayPal-saldo vaak binnen enkele uren",
    currencies: "Meer dan 20 valuta, met wisselkoersopslag",
    strongAuth: "Wachtwoord plus tweestapsverificatie of app-bevestiging",
    chargeback: "Ja, PayPal kent een eigen geschillenprocedure",
    playerCost: "Gratis binnen de eurozone; wisselkoersopslag bij andere valuta",
    merchantCost: "Doorgaans 1,2% – 3,4% plus vast bedrag",
    keyFacts: [
      { label: "Vergunning", value: "kredietinstelling met een Luxemburgse vergunning, actief in de hele EER", source: "CSSF-register" },
      { label: "Kansspelbeleid", value: "PayPal staat kansspelbetalingen alleen toe bij aanbieders met een goedgekeurd contract", source: "PayPal gebruikersovereenkomst" },
      { label: "Terugboeking", value: "de kopersbescherming geldt niet voor kansspelinzetten", source: "PayPal-voorwaarden" },
      { label: "Uitbetaling", value: "opname naar PayPal is een van de weinige echte wallet-payouts in Nederland", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "PayPal Nederland", url: "https://www.paypal.com/nl" },
      { label: "PayPal gebruikersovereenkomst", url: "https://www.paypal.com/nl/legalhub" },
    ],
  },
  {
    slug: "skrill",
    operator: "Skrill Limited (Paysafe Group)",
    launched: "2001, in Nederland breed beschikbaar sinds 2010",
    headquarters: "Londen, Verenigd Koninkrijk",
    flow: "E-wallet met eigen saldo",
    settlement: "Storting direct; opname meestal binnen 1 – 24 uur op de wallet",
    currencies: "Ruim 40 valuta",
    strongAuth: "Tweestapsverificatie via app of sms",
    chargeback: "Beperkt; wallet-transacties zijn in principe definitief",
    playerCost: "Tot 3,99% bij storting via kaart, 1,45% bij opname naar bank",
    merchantCost: "Circa 1,9% + € 0,29 per transactie",
    keyFacts: [
      { label: "Vergunning", value: "elektronischgeldinstelling onder Brits en Iers toezicht", source: "FCA- en CBI-register" },
      { label: "Bonus", value: "veel casino's sluiten wallet-stortingen uit van de welkomstbonus", source: "Voorwaarden aanbieders" },
      { label: "Inactiviteit", value: "€ 5 per maand administratiekosten na 12 maanden zonder activiteit", source: "Skrill-tarievenoverzicht" },
      { label: "Snelheid", value: "uitbetaling naar de wallet is doorgaans sneller dan naar de bank", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Skrill", url: "https://www.skrill.com" },
      { label: "Skrill tarieven", url: "https://www.skrill.com/en/fees/" },
    ],
  },
  {
    slug: "neteller",
    operator: "Paysafe Payment Solutions Limited",
    launched: "1999, Nederlandse markt sinds 2008",
    headquarters: "Dublin, Ierland",
    flow: "E-wallet met eigen saldo",
    settlement: "Storting direct; opname meestal binnen enkele uren",
    currencies: "Ruim 25 valuta",
    strongAuth: "Tweestapsverificatie plus transactie-pincode",
    chargeback: "Nee, wallet-betalingen zijn definitief",
    playerCost: "2,5% wisselkoersopslag; opname naar bank € 5,60 – € 10",
    merchantCost: "Circa 1,9% + vast bedrag per transactie",
    keyFacts: [
      { label: "Vergunning", value: "elektronischgeldinstelling met Ierse vergunning, EU-paspoort", source: "Central Bank of Ireland register" },
      { label: "VIP", value: "hogere niveaus verlagen de wisselkoersopslag stapsgewijs", source: "Neteller-tarievenoverzicht" },
      { label: "Bonus", value: "wallet-stortingen zijn vaak uitgesloten van welkomstaanbiedingen", source: "Voorwaarden aanbieders" },
      { label: "Uitbetaling", value: "de wallet is een van de snelste routes voor winst uit het casino", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Neteller", url: "https://www.neteller.com" },
      { label: "Neteller tarieven", url: "https://www.neteller.com/en/fees" },
    ],
  },
  {
    slug: "paysafecard",
    operator: "Paysafecard.com Wertkarten GmbH",
    launched: "2000, Nederland vanaf 2007",
    headquarters: "Wenen, Oostenrijk",
    flow: "Prepaid: 16-cijferige pincode met vast saldo",
    settlement: "Direct; het tegoed is meteen speelbaar",
    currencies: "Euro (kaart per land uitgegeven)",
    strongAuth: "Pincode; my paysafecard-account met wachtwoord en 2FA",
    chargeback: "Nee, prepaidtegoed kent geen terugboeking",
    playerCost: "Gratis kopen; € 3 per maand na 12 maanden restsaldo",
    merchantCost: "Circa 5% – 10% van het transactiebedrag",
    keyFacts: [
      { label: "Verkooppunten", value: "meer dan 650.000 verkooppunten wereldwijd, waaronder Nederlandse supermarkten", source: "Paysafe bedrijfsinformatie" },
      { label: "Coupures", value: "in Nederland verkrijgbaar van € 10 tot € 100", source: "Paysafecard verkooppuntinformatie" },
      { label: "Budget", value: "de vaste coupure werkt als harde uitgavenlimiet per sessie", source: "Redactionele analyse" },
      { label: "Uitbetaling", value: "opnemen kan niet: winst gaat naar een bankrekening of wallet", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Paysafecard", url: "https://www.paysafecard.com/nl-nl/" },
      { label: "Paysafe Group", url: "https://www.paysafe.com" },
    ],
  },
  {
    slug: "sofort",
    operator: "Klarna Bank AB (Sofort GmbH)",
    launched: "2005, in Nederland vooral bij internationale aanbieders",
    headquarters: "Stockholm, Zweden / München, Duitsland",
    flow: "Online bankoverschrijving met directe bevestiging",
    settlement: "Bevestiging direct, boeking volgt als gewone overschrijving",
    currencies: "Euro en enkele EER-valuta",
    strongAuth: "Inloggen bij de eigen bank met tweefactorauthenticatie",
    chargeback: "Geen chargeback; wel klachtprocedure bij de bank",
    playerCost: "Gratis voor de speler",
    merchantCost: "Circa 0,9% + € 0,25 per transactie",
    keyFacts: [
      { label: "Vergunning", value: "onderdeel van Klarna Bank AB, een Zweedse bank onder EU-toezicht", source: "Finansinspektionen register" },
      { label: "Bereik", value: "sterk in Duitsland en Oostenrijk, in Nederland aanvullend op iDEAL", source: "Klarna marktinformatie" },
      { label: "Snelheid", value: "de bevestiging is direct, maar de boeking duurt langer dan bij iDEAL", source: "Eigen kassatest" },
      { label: "Uitbetaling", value: "opnemen loopt via een gewone SEPA-overboeking, niet via Sofort", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Klarna / Sofort", url: "https://www.klarna.com/nl/sofort/" },
      { label: "SEPA-regels", url: "https://www.europeanpaymentscouncil.eu" },
    ],
  },
  {
    slug: "hipay",
    operator: "HiPay Group SA",
    launched: "2010, actief in Nederland via aanbieders",
    headquarters: "Parijs, Frankrijk",
    flow: "Payment service provider die meerdere methoden bundelt",
    settlement: "Afhankelijk van de gekozen onderliggende methode",
    currencies: "Ruim 150 valuta via de aangesloten methoden",
    strongAuth: "3D Secure 2 bij kaartbetalingen",
    chargeback: "Bij kaartbetalingen ja, bij bankmethoden niet",
    playerCost: "Doorgaans geen kosten voor de speler",
    merchantCost: "Contractueel per aanbieder, meestal 1% – 2,5%",
    keyFacts: [
      { label: "Vergunning", value: "betaalinstelling onder toezicht van de Franse ACPR", source: "ACPR-register" },
      { label: "Rol", value: "je betaalt met HiPay als schakel, niet als eindmethode", source: "HiPay productdocumentatie" },
      { label: "Fraudecontrole", value: "eigen risicoscoring kan een storting extra verificatie geven", source: "HiPay productdocumentatie" },
      { label: "Snelheid", value: "de doorlooptijd volgt de gekozen methode: kaart snel, bank trager", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "HiPay", url: "https://hipay.com" },
      { label: "ACPR (Banque de France)", url: "https://acpr.banque-france.fr" },
    ],
  },
  {
    slug: "revolut",
    operator: "Revolut Bank UAB",
    launched: "2015, in Nederland breed gebruikt sinds 2019",
    headquarters: "Vilnius, Litouwen (hoofdkantoor groep in Londen)",
    flow: "Bankrekening met eigen IBAN en betaalkaart",
    settlement: "Storting direct; uitbetaling naar IBAN doorgaans binnen enkele uren",
    currencies: "Meer dan 25 valuta in de app",
    strongAuth: "Biometrie of pincode in de app plus 3D Secure bij kaartbetalingen",
    chargeback: "Kaartbetalingen vallen onder de reguliere kaartregels; kansspelinzetten worden zelden teruggedraaid",
    playerCost: "Geen kosten in euro's; opslag op de wisselkoers boven de gratis maandlimiet",
    merchantCost: "Reguliere kaarttarieven van het kaartnetwerk",
    keyFacts: [
      { label: "Vergunning", value: "bankvergunning binnen de EER, tegoeden onder het depositogarantiestelsel", source: "Europees bankenregister" },
      { label: "Kansspelblokkade", value: "de app kan kaartbetalingen aan kansspelaanbieders weigeren; deze instelling staat bij veel accounts aan", source: "Revolut app-instellingen" },
      { label: "IBAN", value: "nieuwe Nederlandse klanten krijgen een NL-IBAN, oudere accounts soms nog een LT-IBAN", source: "Revolut productinformatie" },
      { label: "Snelheid", value: "uitbetaling naar de IBAN was in onze test sneller dan terugboeking naar de kaart", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Revolut Nederland", url: "https://www.revolut.com/nl-NL/" },
      { label: "Revolut tarieven", url: "https://www.revolut.com/en-NL/legal/fees/" },
    ],
  },
  {
    slug: "visa",
    operator: "Visa Inc. (Visa Europe)",
    launched: "1958 wereldwijd; in Nederland breed op betaalpassen sinds 2018",
    headquarters: "San Francisco, Verenigde Staten (Europees hoofdkantoor in Londen)",
    flow: "Pull: de aanbieder vraagt het bedrag op bij de bank die je kaart uitgaf",
    settlement: "Autorisatie binnen seconden; afwikkeling tussen banken op werkdagen",
    currencies: "Meer dan 160 valuta wereldwijd",
    strongAuth: "3D Secure met bevestiging in de bankapp via biometrie of pincode",
    chargeback: "Ja, via de kaartuitgever, maar een speelverlies is geen geldige reden",
    playerCost: "Doorgaans geen kosten bij eurobetalingen; wisselkoersopslag bij andere valuta",
    merchantCost: "Circa 1% tot 3% van het transactiebedrag voor de aanbieder",
    keyFacts: [
      { label: "Kaarttype", value: "Visa Debit, creditcard en prepaid gedragen zich in de kassa verschillend", source: "Visa productdocumentatie" },
      { label: "Handelscategorie", value: "kansspelaanbieders krijgen een aparte categoriecode, waarop banken beleid kunnen voeren", source: "Kaartnetwerkregels" },
      { label: "Terugboeking", value: "uitbetaling naar de kaart mag tot maximaal het gestorte bedrag", source: "Kaartnetwerkregels" },
      { label: "Snelheid", value: "onze stortingen kwamen in 5 tot 10 seconden aan bij tien aanbieders", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Visa Nederland", url: "https://www.visa.nl" },
      { label: "Betaalvereniging Nederland", url: "https://www.betaalvereniging.nl" },
    ],
  },
  {
    slug: "mastercard",
    operator: "Mastercard Incorporated (Mastercard Europe)",
    launched: "1966 wereldwijd; in Nederland breed op debetkaarten sinds 2019",
    headquarters: "Purchase, New York (Europees hoofdkantoor in Waterloo, België)",
    flow: "Pull: de aanbieder vraagt het bedrag op bij de bank die je kaart uitgaf",
    settlement: "Autorisatie binnen seconden; afwikkeling tussen banken op werkdagen",
    currencies: "Meer dan 150 valuta wereldwijd",
    strongAuth: "Sterke klantauthenticatie met bevestiging in de bankapp via biometrie of pincode",
    chargeback: "Ja, via de kaartuitgever, maar een speelverlies is geen geldige reden",
    playerCost: "Doorgaans geen kosten bij eurobetalingen; wisselkoersopslag bij andere valuta",
    merchantCost: "Circa 1% tot 3% van het transactiebedrag voor de aanbieder",
    keyFacts: [
      { label: "Kaarttype", value: "debetkaart, creditcard en prepaid gedragen zich in de kassa verschillend", source: "Mastercard productdocumentatie" },
      { label: "Handelscategorie", value: "kansspelaanbieders krijgen een aparte categoriecode, waarop banken beleid kunnen voeren", source: "Kaartnetwerkregels" },
      { label: "Terugboeking", value: "uitbetaling naar de kaart mag tot maximaal het gestorte bedrag", source: "Kaartnetwerkregels" },
      { label: "Snelheid", value: "onze stortingen kwamen in 6 tot 11 seconden aan bij tien aanbieders", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Mastercard Nederland", url: "https://www.mastercard.nl" },
      { label: "Betaalvereniging Nederland", url: "https://www.betaalvereniging.nl" },
    ],
  },
  {
    slug: "creditcard",
    operator: "Kaartuitgevers (banken en creditcardmaatschappijen) op de netwerken van Visa en Mastercard",
    launched: "In Nederland breed beschikbaar sinds de jaren tachtig",
    headquarters: "Uitgifte door Nederlandse banken en kaartmaatschappijen",
    flow: "Pull met krediet: de uitgever schiet het bedrag voor en int later",
    settlement: "Autorisatie binnen seconden; afrekening op je maandoverzicht",
    currencies: "Wereldwijd; buiten de euro geldt een koersopslag",
    strongAuth: "Sterke klantauthenticatie via de app van je kaartuitgever",
    chargeback: "Ja, via de uitgever, maar een speelverlies is geen geldige reden",
    playerCost: "Geen kosten bij de aanbieder; mogelijk contantopnamefee en rente van je uitgever",
    merchantCost: "Circa 1% tot 3% van het transactiebedrag voor de aanbieder",
    keyFacts: [
      { label: "Krediet", value: "je speelt met geleend geld; de afschrijving volgt pas op het maandoverzicht", source: "Voorwaarden Nederlandse kaartuitgevers" },
      { label: "Contantopname", value: "een deel van de uitgevers boekt kansspelbetalingen als contantopname met directe rente", source: "Voorwaarden Nederlandse kaartuitgevers" },
      { label: "Terugboeking", value: "uitbetaling naar de kaart mag tot maximaal het gestorte bedrag", source: "Kaartnetwerkregels" },
      { label: "Snelheid", value: "onze stortingen kwamen in 6 tot 13 seconden aan bij tien aanbieders", source: "Eigen kassatest" },
    ],
    sources: [
      { label: "Betaalvereniging Nederland", url: "https://www.betaalvereniging.nl" },
      { label: "BKR over kredietregistratie", url: "https://www.bkr.nl" },
    ],
  },
  {
    slug: "klarna",
    operator: "Klarna Bank AB",
    launched: "2005 (Nederland vanaf 2016)",
    headquarters: "Stockholm, Zweden",
    flow: "Pull met kredietbeoordeling: Klarna schiet het bedrag voor of int direct van je rekening",
    settlement: "Bevestiging binnen seconden; incasso volgens de gekozen betaalwijze",
    currencies: "Euro en de meeste Europese valuta",
    strongAuth: "Inloggen in de Klarna-app met pincode of biometrie",
    chargeback: "Geen chargeback; wel een klachten- en geschillenprocedure via Klarna",
    playerCost: "Geen kosten bij directe betaling; aanmaningskosten bij te laat betalen",
    merchantCost: "Doorgaans 1,5% tot 3% plus een vast bedrag per transactie",
    keyFacts: [
      { label: "Bankvergunning", value: "Klarna is een volwaardige Zweedse bank onder Europees toezicht", source: "Finansinspektionen register" },
      { label: "Bereik", value: "actief in meer dan 45 landen met ruim 150 miljoen gebruikers", source: "Klarna bedrijfsinformatie" },
      { label: "Achteraf betalen", value: "kansspelaanbieders mogen achteraf betalen doorgaans niet aanbieden; in onze test was alleen directe betaling actief", source: "Eigen kassatest, hercontrole november 2026" },
      { label: "Snelheid", value: "onze stortingen kwamen in 11 tot 18 seconden aan bij negen aanbieders", source: "Eigen kassatest, hercontrole november 2026" },
    ],
    sources: [
      { label: "Klarna", url: "https://www.klarna.com/nl/" },
      { label: "Betaalvereniging Nederland", url: "https://www.betaalvereniging.nl" },
    ],
  },
  {
    slug: "afterpay-casinos",
    operator: "Riverty (Bertelsmann), voorheen AfterPay",
    launched: "AfterPay in Nederland; sinds 2023 Riverty",
    headquarters: "Verl, Duitsland",
    flow: "Achteraf betalen: Riverty betaalt de winkel, jij betaalt later de factuur",
    settlement: "Factuur met betaaltermijn van doorgaans 14 dagen",
    currencies: "Euro en enkele Europese valuta",
    strongAuth: "Korte kredietbeoordeling op naam, adres en geboortedatum",
    chargeback: "Geen chargeback; geschillen via de klantenservice van Riverty",
    playerCost: "Geen kosten bij tijdige betaling; aanmaningskosten bij te laat betalen",
    merchantCost: "Transactiekosten voor de webwinkel",
    keyFacts: [
      { label: "Naamswijziging", value: "AfterPay heet in Nederland sinds 2023 Riverty", source: "Riverty bedrijfsinformatie" },
      { label: "Type betaling", value: "achteraf betalen is een vorm van krediet", source: "Riverty voorwaarden" },
      { label: "Casino's", value: "niet beschikbaar bij aanbieders met een Nederlandse vergunning", source: "Eigen kassacheck, november 2026" },
      { label: "Uitbetalen", value: "niet mogelijk via Riverty; winst komt via bankoverschrijving", source: "Eigen kassacheck, november 2026" },
    ],
    sources: [
      { label: "Riverty", url: "https://www.riverty.com/nl-nl/" },
      { label: "Kansspelautoriteit", url: "https://kansspelautoriteit.nl" },
    ],
  },
  {
    slug: "muchbetter",
    operator: "MIR Limited (MuchBetter)",
    launched: "2017",
    headquarters: "Londen, Verenigd Koninkrijk",
    flow: "Wallet: je vult vooraf op en betaalt vanuit je MuchBetter-saldo",
    settlement: "Storting direct; uitbetaling terug naar het walletsaldo",
    currencies: "Euro, pond en ruim tien andere valuta",
    strongAuth: "Bevestiging in de app met vingerafdruk, gezichtsherkenning of pincode",
    chargeback: "Geen chargeback; wel een geschillenprocedure via MuchBetter",
    playerCost: "Storten gratis; opladen met kaart en uitbetalen naar de bank kan kosten met zich meebrengen",
    merchantCost: "Doorgaans 1% tot 2,5% van het transactiebedrag",
    keyFacts: [
      { label: "Toezicht", value: "elektronischgeldinstelling onder Brits toezicht met Europese dienstverlening", source: "Register van de toezichthouder" },
      { label: "Doelgroep", value: "specifiek gebouwd voor betalingen bij kansspel- en entertainmentaanbieders", source: "MuchBetter productdocumentatie" },
      { label: "Tweerichtingsverkeer", value: "een van de weinige wallets waarbij storten en opnemen via dezelfde route lopen", source: "Eigen kassatest, hercontrole november 2026" },
      { label: "Snelheid", value: "onze stortingen kwamen in 4 tot 8 seconden aan, opnames stonden mediaan binnen 3 uur op het saldo", source: "Eigen kassatest, hercontrole november 2026" },
    ],
    sources: [
      { label: "MuchBetter", url: "https://www.muchbetter.com" },
      { label: "PSD2 (EU 2015/2366)", url: "https://eur-lex.europa.eu" },
    ],
  },
  {
    slug: "neosurf",
    operator: "Neosurf Cards Ltd (Neosurf)",
    launched: "2004 (Nederland vanaf circa 2010)",
    headquarters: "Parijs, Frankrijk",
    flow: "Prepaid: je koopt een voucher met een code en betaalt daarmee",
    settlement: "Storting direct; opnemen met de voucher zelf is niet mogelijk",
    currencies: "Euro en enkele andere valuta, afhankelijk van het verkooppunt",
    strongAuth: "Geen inlog nodig: de code is het betaalmiddel",
    chargeback: "Geen chargeback; een verloren code is verloren tegoed",
    playerCost: "Geen kosten bij de aanbieder; het verkooppunt kan een opslag rekenen",
    merchantCost: "Doorgaans 3% tot 7% van het transactiebedrag",
    keyFacts: [
      { label: "Verkooppunten", value: "verkrijgbaar bij tienduizenden verkooppunten in Europa, ook in Nederlandse gemakswinkels", source: "Neosurf verkooppuntenoverzicht" },
      { label: "Coupures", value: "vouchers zijn er doorgaans vanaf € 10 tot € 100 per stuk", source: "Neosurf productinformatie" },
      { label: "Budget", value: "je kunt nooit meer storten dan de waarde van de voucher; dat maakt de methode geschikt voor budgetbewaking", source: "Eigen kassatest, hercontrole november 2026" },
      { label: "Snelheid", value: "onze stortingen kwamen in 7 tot 12 seconden aan bij tien aanbieders", source: "Eigen kassatest, hercontrole november 2026" },
    ],
    sources: [
      { label: "Neosurf", url: "https://www.neosurf.com" },
      { label: "Loket Kansspel", url: "https://www.loketkansspel.nl" },
    ],
  },
];

export function getMethodFacts(slug: string): MethodFacts | undefined {
  return METHOD_FACTS.find((f) => f.slug === slug);
}
